import { useState, useEffect, createContext, useContext, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { User, Session } from "@supabase/supabase-js";
import { useToast } from "@/hooks";
import { useOptimizedCache } from "@/hooks/performance/useOptimizedCache";
import { useNavigate } from "react-router-dom";

interface UserProfile {
  role: string;
  school_id: string;
  student_id: string | null;
  sections: string[];
  email: string;
  full_name?: string;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  userProfile: UserProfile | null;
  loading: boolean;
  signIn: (
    email: string,
    password: string
  ) => Promise<{ error: any; data: any }>;
  setUserProfile: React.Dispatch<React.SetStateAction<UserProfile>>;
  signUp: (
    email: string,
    password: string,
    fullName?: string,
    role?: string
  ) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    // عودة بيانات افتراضية بدلاً من رمي خطأ لمنع crash التطبيق
    console.warn(
      "useAuth called outside AuthProvider, returning default values"
    );
    return {
      user: null,
      session: null,
      userProfile: null,
      loading: true,
      signIn: async () => ({
        error: new Error("Auth not initialized"),
        data: null,
      }),
      signUp: async () => ({ error: new Error("Auth not initialized") }),
      signOut: async () => {},
      refreshProfile: async () => {},
      setUserProfile: () => {},
    };
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const sessionInitialized = useRef(false);
  const cache = useOptimizedCache<UserProfile>({ ttl: 5 * 60 * 1000 }); // 5 دقائق cache
  // const navigate = useNavigate();

  // console.log("AuthProvider rendered, user:", user, "profile:", userProfile);
  useEffect(() => {
    // let mounted = true;
    // Set up auth state listener FIRST
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === "SIGNED_OUT") {
        setSession(null);
        setUser(null);
        setUserProfile(null);
        return;
      }
      if (event === "SIGNED_IN" || event === "TOKEN_REFRESHED") {
        if (!session?.user) {
          setSession(null);
          setUser(null);
          return;
        }
        if (session.user.new_email && event === "SIGNED_IN") {
          setTimeout(async () => {
            const {
              data: { session: freshSession },
            } = await supabase.auth.refreshSession();
            console.log("Fresh session:", event, freshSession);
            setSession(freshSession);
            const {
              data: { user },
            } = await supabase.auth.getUser();
            // console.log("User after updated:", event, user);

            setUser(user);
          }, 0);
        } else {
          setSession(session);
          setUser(session?.user ?? null);
        }
      }
    });

    // THEN check for existing session with deduplication
    const initializeAuth = async () => {
      if (sessionInitialized.current) return;
      sessionInitialized.current = true;

      try {
        const {
          data: { session },
          error,
        } = await supabase.auth.getSession();

        if (error) {
          console.error("Error getting session:", error);
        }

        // if (mounted) {
        setSession(session);
        setUser(session?.user ?? null);
        // جلب معلومات الملف الشخصي إذا كان المستخدم موجود
        if (session?.user) {
          // console.log("fetch user profile");
          // console.log("Auth user:", session.user);
          await fetchUserProfile(session.user.id);
        }
        setLoading(false);
        // }
      } catch (error) {
        console.error("Failed to initialize auth:", error);
        // if (mounted) {
        setSession(null);
        setUser(null);
        setUserProfile(null);

        // setLoading(false);
      }
      // }
    };

    initializeAuth();

    // return () => {
    //   // mounted = false;
    //   // subscription.unsubscribe();
    // };
  }, []);

  useEffect(() => {
    if (user?.id) {
      fetchUserProfile(user.id);
    } 
    // else setLoading(false);
  }, [user]);
  // دالة موحدة لجلب معلومات الملف الشخصي
  const fetchUserProfile = async (userId: string) => {
    try {
      // const cacheKey = `profile_${userId}`;

      // فحص cache أولاً
      // const cachedProfile = cache.get(cacheKey);
      // if (cachedProfile) {
      //   setUserProfile(cachedProfile);
      //   return;
      // }

      // جلب جميع البيانات المطلوبة في طلب واحد
      const { data, error } = await supabase
        .from("profiles")
        .select("role, school_id, sections, student_id, email, full_name")
        .eq("id", userId)
        .single();

      if (error) {
        console.error("Error fetching user profile:", error);
        // setLoading(false);

        return;
      }

      if (data) {
        const profile: UserProfile = {
          role: data.role || "school",
          school_id: data.school_id || userId,
          student_id: data.student_id || null,
          sections: data?.sections?.['sections'] || [],
          email: data.email,
          full_name: data.full_name,
        };

        // حفظ في cache
        // cache.set(cacheKey, profile);
        setUserProfile(profile);
        setLoading(false);
      }
    } catch (error) {
      console.error("Failed to fetch user profile:", error);
      // setLoading(false);
    }
  };

  const refreshProfile = async () => {
    if (user?.id) {
      // مسح cache وإعادة جلب البيانات
      cache.clear();
      await fetchUserProfile(user.id);
    }
  };

  const signIn = async (email: string, password: string) => {
    const loginAttemptId = crypto.randomUUID();
    const normalizedEmail = (email || "").trim().toLowerCase();
    const startedAt = performance.now();

    console.info("[Auth.signIn] Attempt started", {
      loginAttemptId,
      inputEmail: email,
      normalizedEmail,
      passwordLength: password?.length ?? 0,
      timestamp: new Date().toISOString(),
    });

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: normalizedEmail,
        password,
      });

      const elapsedMs = Math.round(performance.now() - startedAt);

      if (error) {
        console.error("[Auth.signIn] Supabase signInWithPassword failed", {
          loginAttemptId,
          elapsedMs,
          message: error.message,
          name: error.name,
          status: (error as any)?.status,
          code: (error as any)?.code,
          rawError: error,
          serializedError: JSON.stringify(error, Object.getOwnPropertyNames(error)),
          normalizedEmail,
        });

        toast({
          title: "خطأ في تسجيل الدخول",
          description:
            error.message === "Invalid login credentials"
              ? "البريد الإلكتروني أو كلمة المرور غير صحيحة"
              : error.message,
          variant: "destructive",
        });
      } else {
        console.info("[Auth.signIn] Supabase signInWithPassword success", {
          loginAttemptId,
          elapsedMs,
          userId: data?.user?.id,
          sessionExists: !!data?.session,
          normalizedEmail,
        });
        toast({
          title: "تم تسجيل الدخول بنجاح",
          description: "مرحباً بك في المنصة",
        });
      }

      return { error, data };
    } catch (error) {
      console.error("[Auth.signIn] Unexpected exception during sign-in", {
        loginAttemptId,
        normalizedEmail,
        exception: error,
      });
      return { error, data: null };
    }
  };

  const signUp = async (
    email: string,
    password: string,
    fullName?: string,
    role: string = "school"
  ) => {
    try {
      const redirectUrl = `${window.location.origin}/`;

      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: redirectUrl,
          data: {
            full_name: fullName,
            role: role,
          },
        },
      });

      if (error) {
        toast({
          title: "خطأ في إنشاء الحساب",
          description: error.message,
          variant: "destructive",
        });
      } else {
        toast({
          title: "تم إنشاء الحساب بنجاح",
          description: "يرجى التحقق من بريدك الإلكتروني لتفعيل الحساب",
        });
      }

      return { error };
    } catch (error) {
      return { error };
    }
  };

  const signOut = async () => {
    try {
      // مسح البيانات المحلية والكاش أولاً
      localStorage.removeItem("userType");
      localStorage.removeItem("isLoggedIn");
      localStorage.removeItem("isAdminAccess");
      localStorage.removeItem("adminAccessSchool");
      localStorage.removeItem("currentSchoolId");
      localStorage.removeItem("adminEmail");
      localStorage.removeItem("siteLogo");
      cache.clear();

      // يجب دائماً استدعاء signOut على العميل: مساحة التخزين (مفتاح supabase.auth.token)
      // لا تُمسح إذا اكتفينا بتحديث حالة React فقط، فيُعاد جلب الجلسة بعد ثوانٍ.
      const { error } = await supabase.auth.signOut();
      if (error) {
        localStorage.removeItem("supabase.auth.token");
      }

      // تحديث الحالة المحلية
      setSession(null);
      setUser(null);
      setUserProfile(null);

      toast({
        title: "تم تسجيل الخروج بنجاح",
        description: "نراك قريباً",
      });

      // التوجيه إلى صفحة تسجيل الدخول
      // setTimeout(() => {
      //   window.location.href = "/auth";
      // }, 1000);
    } catch (error) {
      // حتى في حالة الخطأ، نقوم بمسح البيانات المحلية والتوجيه
      localStorage.clear();
      cache.clear();
      setSession(null);
      setUser(null);
      setUserProfile(null);

      toast({
        title: "تم تسجيل الخروج",
        description: "تم مسح جميع البيانات المحلية",
      });

      // setTimeout(() => {
      //   window.location.href = "/auth";
      // }, 1000);
    }
  };

  const value = {
    user,
    setUserProfile,
    session,
    userProfile,
    loading,
    signIn,
    signUp,
    signOut,
    refreshProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
